import { c as _c } from "react/compiler-runtime";
import { setDateWithTimezone, Skeleton, toISOStringWithoutTimezone } from '@systemeio/components';
import { ContactFieldValueInterface, ContactSlugEnum } from '@systemeio/types';
import debounce from 'lodash.debounce';
import { ContactFilterDateConditionEnum, ContactSearchFieldEnum } from 'modules/contacts/enums/contact-enum';
import { ContactId } from 'modules/contacts/types/contact-interface';
import { getConditions } from 'modules/contacts/utils/get-conditions';
import React, { useCallback, useEffect, type JSX } from 'react';
import { Tooltip } from 'shared/components';
import { InfoRow } from 'shared/components/info-row';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { ContactCardInfoInterface, ContactInfoCard } from 'shared/components/tooltip/contact-info-card-tooltip';
import { pageRoute } from 'shared/constants/routes.constants';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { QueryKeysEnum } from 'shared/route-query/enum/query-keys-enum';
import { OrderEnum } from 'shared/types/pagination-interface';
import { dateFormat } from 'shared/utils/date-time-format';
import { getPageRoute } from 'shared/utils/get-dynamic-page-route';
import { ContactTooltipDataInterface, useContactsTooltipData } from '../hooks/use-contacts-tooltip-data';
interface ContactInfoTooltipProps {
  children?: JSX.Element | string;
  contactId?: ContactId;
  removedAt: string | null;
}
const getEmail = (fieldValues: ContactFieldValueInterface[]) => fieldValues.find(el => el.contactField.slug === ContactSlugEnum.Email)?.value;
const toContactCardInfo = ({
  fieldValues,
  referredBy
}: ContactTooltipDataInterface): ContactCardInfoInterface => ({
  email: getEmail(fieldValues) ?? '',
  fieldValues,
  referredBy: referredBy && {
    email: getEmail(referredBy.fieldValues)
  }
});

/* A removed contact has no page of its own: it links to the bin, filtered by its removal day. */
const getContactHref = (contactId: ContactId, removedAt: string | null) => {
  if (!removedAt) return getPageRoute(pageRoute.ContactDetails, {
    contactId
  });
  const removedOn = toISOStringWithoutTimezone(setDateWithTimezone(removedAt, 'UTC')).split('T')[0];
  const conditions = getConditions({
    filter: {
      [ContactSearchFieldEnum.removed_at]: [{
        compareOperation: ContactFilterDateConditionEnum.equals,
        searchValue: `${removedOn}T00:00:00`
      }]
    }
  });
  return `${getPageRoute(pageRoute.ContactsBin)}?${new URLSearchParams({
    [QueryKeysEnum.Order]: OrderEnum.prev,
    [QueryKeysEnum.Filter]: JSON.stringify(conditions)
  })}`;
};
const LabelTooltipSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col gap-2"><Skeleton className="w-[250px] rounded-md text-transparent">.</Skeleton><Skeleton className="w-[250px] rounded-md text-transparent">.</Skeleton><Skeleton className="w-[250px] rounded-md text-transparent">.</Skeleton><Skeleton className="w-[250px] rounded-md text-transparent">.</Skeleton></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const LazyContactInfoTooltip = ({
  children,
  contactId,
  removedAt
}: ContactInfoTooltipProps) => {
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const {
    contactData,
    fetchContactData,
    clearContactData
  } = useContactsTooltipData();
  const fetchContactDataWithDebounce = useCallback(debounce((id: ContactId) => {
    fetchContactData(id);
  }, 500), [fetchContactData]);
  useEffect(() => {
    return () => {
      fetchContactDataWithDebounce.cancel();
    };
  }, [fetchContactDataWithDebounce]);
  const handleMouseEnter = () => {
    if (contactId) {
      fetchContactDataWithDebounce(contactId);
    }
  };
  const handleMouseLeave = () => {
    fetchContactDataWithDebounce.cancel();
    clearContactData();
  };
  return <span onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Tooltip label={contactData ? <ContactInfoCard contact={toContactCardInfo(contactData)}>
              {removedAt && <InfoRow label={t('global.contact_removed')} value={dateFormat(removedAt, {
        dateFormat: user?.dateFormat,
        timeZone: user?.timezone
      })} />}
            </ContactInfoCard> : <LabelTooltipSkeleton />} mode="hover" triggerProps={{
      render: <span />
    }}>
        {!contactId ? children : <LinkWithoutPrefetch href={getContactHref(contactId, removedAt)} className="hover:underline">
            {children}
          </LinkWithoutPrefetch>}
      </Tooltip>
    </span>;
};
export default LazyContactInfoTooltip;